import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Print as PrintIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { Stepper, Step, StepLabel } from '@mui/material'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, LineChart, Line } from 'recharts'
import api, { reportsApi } from '../../services/api'
import EvaluationDetailView from '../../components/Evaluation/EvaluationDetailView'
import { extractErrorMessage } from '../../utils/errorHandler'
import { formatDateJST } from '../../utils/dateUtils'
import SalesDashboardPage from '../Sales/SalesDashboardPage'
import type { TrueHeatmapData, CategoryDistributionData, SkillTrendData } from '../../types'
import {
  getStatusLabel,
  PROGRESS_STEPS,
  getProgressStep,
  canViewSalaryRate,
  EVALUATION_FUNNEL_STAGES,
  getFunnelStageCount,
  type EvaluationStatus,
  type GoalApprovalStatus
} from '../../utils/statusLabels'
import { useAuth } from '../../contexts/AuthContext'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

const ReportPage: React.FC = () => {
  const { user, evaluatorInfo, skillPermissions, salesPermissions } = useAuth()
  const [activeTab, setActiveTab] = useState(0)

  // 権限チェック
  const isInEvaluationRoute = evaluatorInfo?.is_self_evaluator ||
    evaluatorInfo?.is_manager_evaluator ||
    evaluatorInfo?.is_dept_head_evaluator

  const canViewSalesDashboard = salesPermissions?.can_view_dashboard

  const canViewSkillMap = skillPermissions.some(
    p => p.permission_type === 'skill_map_viewer'
  )

  // 表示するタブの定義（順序: 営業ダッシュボード → スキルマップ → 評価進捗 → パフォーマンス推移 → 昇給率シミュレーション）
  const visibleTabs = useMemo(() => {
    const tabs: { key: string; icon: React.ReactElement; label: string }[] = []
    if (canViewSalesDashboard) {
      tabs.push({ key: 'sales-dashboard', icon: <TrendingUpIcon />, label: '営業ダッシュボード' })
    }
    if (canViewSkillMap) {
      tabs.push({ key: 'skill-map', icon: <BarChartIcon />, label: 'スキルマップ可視化・分析' })
    }
    if (isInEvaluationRoute) {
      tabs.push({ key: 'evaluation-progress', icon: <CheckCircleIcon />, label: '評価進捗' })
      tabs.push({ key: 'performance-trends', icon: <TrendingUpIcon />, label: 'パフォーマンス推移' })
      tabs.push({ key: 'salary-simulation', icon: <CalculateIcon />, label: '昇給率シミュレーション' })
    }
    return tabs
  }, [isInEvaluationRoute, canViewSalesDashboard, canViewSkillMap])

  const currentTabKey = visibleTabs[activeTab]?.key

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 評価進捗データ
  const [myEvaluationData, setMyEvaluationData] = useState<any>(null)
  const [showMyDetail, setShowMyDetail] = useState(false)
  const [evaluatorAssignments, setEvaluatorAssignments] = useState<any[]>([])
  const [evaluatorAnalytics, setEvaluatorAnalytics] = useState<any>(null)
  // 全社評価進捗（HR向け）
  const [orgProgress, setOrgProgress] = useState<any>(null)
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [availablePeriods, setAvailablePeriods] = useState<Array<{ period_id: string; period_name: string }>>([])
  const [employeeHistoryDialog, setEmployeeHistoryDialog] = useState(false)
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState<any[]>([])
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('')
  const [employeeDetailDialog, setEmployeeDetailDialog] = useState(false)
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState<any>(null)

  // パフォーマンス推移データ
  const [trendsData, setTrendsData] = useState<any[]>([])

  // 昇給率シミュレーションデータ
  const [simulationParams, setSimulationParams] = useState({
    performanceScore: 6,
    yearsOfService: 3,
    companyAdjustmentRate: 5,
  })
  const [simulationResult, setSimulationResult] = useState<any>(null)

  // スキルマップ可視化データ
  const [skillMapData, setSkillMapData] = useState<any>(null)
  const [skillMapStats, setSkillMapStats] = useState<any>(null)
  const [categoryDistribution, setCategoryDistribution] = useState<any>(null)
  const [skillProgress, setSkillProgress] = useState<any>(null)
  const [skillRadarData, setSkillRadarData] = useState<any>(null)
  const [skillHeatmapData, setSkillHeatmapData] = useState<any>(null)
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>('all')
  const [departments, setDepartments] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [jobTitles, setJobTitles] = useState<string[]>([])
  const [skillMapLoading, setSkillMapLoading] = useState(false)
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set())

  // スキルマップヒートマップフィルタリング関連
  const [skillFilter, setSkillFilter] = useState<number | null>(null)
  const [levelFilter, setLevelFilter] = useState<number | null>(null)
  const [employeeSearchText, setEmployeeSearchText] = useState('')
  const [displayLimit, setDisplayLimit] = useState(50)

  // カテゴリ分布ヒートマップフィルタリング関連
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null)
  const [categoryName, setCategoryName] = useState<string | null>(null)
  const [categoryLevelFilter, setCategoryLevelFilter] = useState<string | null>(null)

  // Phase 2 & 3: スキル検索・分析データ
  const [skillSearchResults, setSkillSearchResults] = useState<any>(null)
  const [gapAnalysisData, setGapAnalysisData] = useState<any>(null)
  const [gapAnalysisError, setGapAnalysisError] = useState<string | null>(null)
  const [shortageRankingData, setShortageRankingData] = useState<any>(null)
  const [searchPanelOpen, setSearchPanelOpen] = useState(false)
  const [selectedSkills, setSelectedSkills] = useState<number[]>([])
  const [searchMatchMode, setSearchMatchMode] = useState<'all' | 'any'>('any')
  const [searchMinLevel, setSearchMinLevel] = useState<string>('')
  const [gapAnalysisTarget, setGapAnalysisTarget] = useState<'department' | 'employee'>('department')
  const [gapTargetLevel, setGapTargetLevel] = useState<string>('B')

  // 新しいスキルマップ可視化データ（成果物10工程細分化対応）
  const [trueHeatmapData, setTrueHeatmapData] = useState<TrueHeatmapData | null>(null)
  const [categoryDistData, setCategoryDistData] = useState<CategoryDistributionData | null>(null)
  const [trendData, setTrendData] = useState<SkillTrendData | null>(null)
  const [trendMonths, setTrendMonths] = useState<number>(12)

  // 従業員履歴書ダイアログ用State
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false)
  const [employeeResumeData, setEmployeeResumeData] = useState<any>(null)
  const [resumeLoading, setResumeLoading] = useState(false)

  // カテゴリ別平均スコア推移データ
  const [categoryTrendData, setCategoryTrendData] = useState<any>(null)
  const [categoryTrendMonths, setCategoryTrendMonths] = useState<number>(12)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // 評価期間一覧の取得
  const loadEvaluationPeriods = async () => {
    try {
      const response = await reportsApi.getEvaluationPeriods()
      if (response.data.success && response.data.data) {
        setAvailablePeriods(response.data.data)
        if (response.data.data.length > 0 && !selectedPeriod) {
          setSelectedPeriod(response.data.data[0].period_id)
        }
      }
    } catch (err) {
      console.error('評価期間の取得に失敗しました:', err)
    }
  }

  // 自分の評価進捗の取得
  const loadMyEvaluation = async () => {
    try {
      setLoading(true)
      const response = await reportsApi.getMyEvaluationProgress(selectedPeriod || undefined)
      if (response.data.success) {
        setMyEvaluationData(response.data.data)
      }
    } catch (err) {
      // Silently handle - user may not have evaluation data
      setMyEvaluationData(null)
    } finally {
      setLoading(false)
    }
  }

  // 全社評価進捗（HR向け）の取得
  const loadOrgProgress = async (period_id?: string) => {
    if (!user?.roles?.includes('hr')) return
    try {
      const response = await reportsApi.getOrgProgress(period_id || undefined)
      if (response.data.success) {
        setOrgProgress(response.data.data)
      }
    } catch (err) {
      setOrgProgress(null)
    }
  }

  // 評価者として担当している従業員データの取得
  const loadEvaluatorData = async (period_id: string) => {
    if (!period_id) return

    try {
      setLoading(true)
      const [assignmentsRes, analyticsRes] = await Promise.all([
        reportsApi.getEvaluatorAssignments(period_id),
        reportsApi.getEvaluatorAnalytics(period_id)
      ])

      if (assignmentsRes.data.success) {
        setEvaluatorAssignments(assignmentsRes.data.data || [])
      }
      if (analyticsRes.data.success) {
        setEvaluatorAnalytics(analyticsRes.data.data)
      }
    } catch (err) {
      setEvaluatorAssignments([])
      setEvaluatorAnalytics(null)
    } finally {
      setLoading(false)
    }
  }

  // 従業員の評価履歴を取得
  const loadEmployeeHistory = async (employeeId: string, employeeName: string) => {
    try {
      setLoading(true)
      const response = await reportsApi.getEmployeeHistory(employeeId, 5)
      if (response.data.success) {
        setSelectedEmployeeHistory(response.data.data || [])
        setSelectedEmployeeName(employeeName)
        setEmployeeHistoryDialog(true)
      }
    } catch (err) {
      setError(extractErrorMessage(err, '履歴の取得に失敗しました'))
    } finally {
      setLoading(false)
    }
  }

  // 従業員の評価詳細を取得
  const loadEmployeeDetail = async (employeeId: string, periodId: string) => {
    try {
      setLoading(true)
      const response = await reportsApi.getEvaluationDetail(employeeId, periodId)
      if (response.data.success) {
        setSelectedEmployeeDetail(response.data.data)
        setEmployeeDetailDialog(true)
      }
    } catch (err) {
      setError(extractErrorMessage(err, '評価詳細の取得に失敗しました'))
    } finally {
      setLoading(false)
    }
  }

  // パフォーマンス推移の取得（自分の全履歴）
  const loadPerformanceTrends = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await reportsApi.getPerformanceTrends()

      if (response.data.success && response.data.data) {
        setTrendsData(response.data.data)
      } else {
        setError('パフォーマンス推移の取得に失敗しました')
      }
    } catch (err) {
      console.error('パフォーマンス推移取得エラー:', err)
      setError(extractErrorMessage(err, 'パフォーマンス推移の取得に失敗しました'))
    } finally {
      setLoading(false)
    }
  }

  // 昇給率シミュレーション
  const runSalarySimulation = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await reportsApi.getSalaryIncreaseSimulation(simulationParams)

      if (response.data.success && response.data.data) {
        setSimulationResult(response.data.data)
      } else {
        setError('昇給率シミュレーションに失敗しました')
      }
    } catch (err) {
      console.error('昇給率シミュレーションエラー:', err)
      setError(extractErrorMessage(err, '昇給率シミュレーションに失敗しました'))
    } finally {
      setLoading(false)
    }
  }

  // スキルマップデータの取得
  const loadSkillMapData = async () => {
    try {
      setSkillMapLoading(true)

      // 部門リスト、カテゴリリストを取得
      const [deptResponse, catResponse] = await Promise.all([
        api.get('/departments'),
        api.get('/skills/categories')
      ])

      setDepartments(deptResponse.data?.data || [])
      setCategories(catResponse.data?.data || [])

      // 役職リストを取得（エラーでも他の読み込みに影響しないよう個別取得）
      try {
        const jobTitleResponse = await api.get('/skills/skill-map/job-titles')
        if (jobTitleResponse.data?.success) {
          setJobTitles(jobTitleResponse.data.data || [])
        }
      } catch (jobTitleError) {
        console.error('役職一覧取得エラー:', jobTitleError)
      }

      // マトリクスデータと統計データを取得
      await loadSkillMapMatrix()
      await loadSkillMapStats()
      await loadCategoryDistribution()
      await loadSkillProgress()
      await loadSkillRadarData()
      await loadSkillHeatmapData()
      await loadShortageRanking()
      await loadGapAnalysis()
      await loadCategoryTrend()

      // 新しいスキルマップ可視化データも取得
      await loadTrueHeatmap()
      await loadCategoryDistributionEnhanced()
      await loadTrendAnalysis()

    } catch (error) {
      console.error('スキルマップデータ取得エラー:', error)
    } finally {
      setSkillMapLoading(false)
    }
  }

  // スキルマトリクスデータの取得
  const loadSkillMapMatrix = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') params.departmentId = selectedDepartment
      if (selectedCategory && selectedCategory !== 'all') params.categoryId = selectedCategory
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/matrix', {
        params
      })

      setSkillMapData(response.data)
    } catch (error) {
      console.error('スキルマトリクス取得エラー:', error)
    }
  }

  // スキル統計データの取得
  const loadSkillMapStats = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') params.departmentId = selectedDepartment
      if (selectedCategory && selectedCategory !== 'all') params.categoryId = selectedCategory
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/stats', {
        params
      })

      setSkillMapStats(response.data)
    } catch (error) {
      console.error('スキル統計取得エラー:', error)
    }
  }

  // カテゴリ別分布データの取得
  const loadCategoryDistribution = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') params.departmentId = selectedDepartment
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/category-level-distribution', {
        params
      })

      setCategoryDistribution(response.data)
    } catch (error) {
      console.error('カテゴリ別分布取得エラー:', error)
    }
  }

  // スキル進捗データの取得
  const loadSkillProgress = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') params.departmentId = selectedDepartment
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/progress', {
        params
      })

      setSkillProgress(response.data)
    } catch (error) {
      console.error('スキル進捗取得エラー:', error)
    }
  }

  // レーダーチャートデータの取得
  const loadSkillRadarData = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentIds = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/radar', {
        params
      })

      if (response.data.success) {
        setSkillRadarData(response.data.data)
      }
    } catch (error) {
      console.error('レーダーチャートデータ取得エラー:', error)
    }
  }

  // ヒートマップデータの取得
  const loadSkillHeatmapData = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedCategory && selectedCategory !== 'all') {
        params.categoryId = selectedCategory
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle
      // 表示上限を設定
      params.limit = displayLimit

      console.log('[DEBUG] Loading heatmap data with params:', params)
      const response = await api.get('/skills/skill-map/heatmap', {
        params
      })

      console.log('[DEBUG] Heatmap response:', response.data)
      if (response.data.success) {
        setSkillHeatmapData(response.data.data)
        console.log('[DEBUG] Heatmap data set:', response.data.data)
      }
    } catch (error) {
      console.error('ヒートマップデータ取得エラー:', error)
    }
  }

  // ヒートマップセルクリックハンドラ
  const handleHeatmapCellClick = (skillId: number, skillName: string, level: number) => {
    setSkillFilter(skillId)
    setLevelFilter(level)

    // ヒートマップセクションまでスムーズスクロール
    setTimeout(() => {
      const heatmapSection = document.getElementById('skillmap-heatmap-section')
      if (heatmapSection) {
        heatmapSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // フィルタリセットハンドラ
  const handleResetHeatmapFilter = () => {
    setSkillFilter(null)
    setLevelFilter(null)
    setEmployeeSearchText('')
    setCategoryFilter(null)
    setCategoryName(null)
    setCategoryLevelFilter(null)
  }

  // カテゴリ分布ヒートマップセルクリックハンドラ
  const handleCategoryHeatmapCellClick = (categoryId: number, categoryName: string, level: string) => {
    setCategoryFilter(categoryId)
    setCategoryName(categoryName)
    setCategoryLevelFilter(level)
    // スキルフィルタをリセット（カテゴリフィルタと同時使用しない）
    setSkillFilter(null)
    setLevelFilter(null)

    // ヒートマップセクションまでスムーズスクロール
    setTimeout(() => {
      const heatmapSection = document.getElementById('skillmap-heatmap-section')
      if (heatmapSection) {
        heatmapSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // フィルタリングされたヒートマップデータ
  const filteredHeatmapData = useMemo(() => {
    if (!skillHeatmapData) return null

    // フィルタが設定されていない場合は元のデータをそのまま返す
    if (skillFilter === null && levelFilter === null && !employeeSearchText && categoryFilter === null && categoryName === null && categoryLevelFilter === null) {
      return skillHeatmapData
    }

    // カテゴリフィルタが設定されている場合、スキル列も絞り込むためのインデックスを取得
    let categorySkillIndices: number[] | null = null
    if (categoryName !== null && skillHeatmapData.categoryInfo && Array.isArray(skillHeatmapData.categoryInfo)) {
      categorySkillIndices = skillHeatmapData.categoryInfo
        .filter((info: any) => info.categoryName === categoryName)
        .map((info: any) => {
          return skillHeatmapData.skills.findIndex((s: any) => s.id === info.skillId)
        })
        .filter((idx: number) => idx !== -1)
    }

    // フィルタリング処理
    const filteredEmployees: any[] = []
    const filteredMatrix: number[][] = []

    skillHeatmapData.employees.forEach((employee: any, empIndex: number) => {
      let shouldInclude = true

      // スキル名検索フィルタ
      if (employeeSearchText && !employee.name.includes(employeeSearchText)) {
        shouldInclude = false
      }

      // スキルフィルタ（該当スキルを持っているか）
      if (shouldInclude && skillFilter !== null && levelFilter !== null) {
        const skillIndex = skillHeatmapData.skills.findIndex((s: any) => s.id === skillFilter)
        if (skillIndex !== -1) {
          const employeeLevel = skillHeatmapData.matrix[empIndex][skillIndex]
          if (employeeLevel !== levelFilter) {
            shouldInclude = false
          }
        } else {
          shouldInclude = false
        }
      }

      // カテゴリフィルタ（該当カテゴリの平均レベルが該当レベルの範囲か）
      if (shouldInclude && categoryName !== null && categoryLevelFilter !== null) {
        // categoryInfo の存在確認
        if (!skillHeatmapData.categoryInfo || !Array.isArray(skillHeatmapData.categoryInfo)) {
          console.warn('categoryInfo is not available in skillHeatmapData')
          // categoryInfo が存在しない場合はカテゴリフィルタをスキップ（全員表示）
        } else if (categorySkillIndices && categorySkillIndices.length > 0) {
          // カテゴリ内全スキルのスコアを取得
          const scores = categorySkillIndices.map((skillIndex: number) => {
            return skillHeatmapData.matrix[empIndex][skillIndex]
          })

          // 有効なスコア（> 0）のみで平均を計算
          const validScores = scores.filter((s: number) => s > 0)

          if (validScores.length === 0) {
            shouldInclude = false
          } else {
            // 平均スコアを計算
            const avgScore = validScores.reduce((a: number, b: number) => a + b, 0) / validScores.length

            // 平均スコアをレベルに変換（境界値は下位レベル: >4=S, >3=A, >2=B, >1=C）
            let avgLevel: string
            if (avgScore > 4) avgLevel = 'S'
            else if (avgScore > 3) avgLevel = 'A'
            else if (avgScore > 2) avgLevel = 'B'
            else if (avgScore > 1) avgLevel = 'C'
            else avgLevel = 'D'

            // フィルタ条件: 平均レベル === クリックしたレベル
            if (avgLevel !== categoryLevelFilter) {
              shouldInclude = false
            }
          }
        } else {
          shouldInclude = false
        }
      }

      if (shouldInclude) {
        filteredEmployees.push(employee)
        // カテゴリフィルタがある場合は該当カテゴリのスキル列のみに絞り込む
        if (categorySkillIndices && categorySkillIndices.length > 0) {
          const filteredRow = categorySkillIndices.map((idx: number) => skillHeatmapData.matrix[empIndex][idx])
          filteredMatrix.push(filteredRow)
        } else {
          filteredMatrix.push(skillHeatmapData.matrix[empIndex])
        }
      }
    })

    // フィルタリング結果が空の場合は元のデータを返す
    if (filteredEmployees.length === 0) {
      console.warn('フィルタリング結果が0件です。元のデータを表示します。')
      return skillHeatmapData
    }

    // カテゴリフィルタがある場合はスキル列も絞り込む
    let filteredSkills = skillHeatmapData.skills
    if (categorySkillIndices && categorySkillIndices.length > 0) {
      filteredSkills = categorySkillIndices.map((idx: number) => skillHeatmapData.skills[idx])
    }

    return {
      ...skillHeatmapData,
      employees: filteredEmployees,
      skills: filteredSkills,
      matrix: filteredMatrix
    }
  }, [skillHeatmapData, skillFilter, levelFilter, employeeSearchText, categoryFilter, categoryName, categoryLevelFilter])

  // スキル不足ランキングデータの取得
  const loadShortageRanking = async () => {
    try {
      const params: any = {}
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      console.log('[DEBUG] Loading shortage ranking data with params:', params)
      const response = await api.get('/skills/skill-map/shortage-ranking', {
        params
      })

      console.log('[DEBUG] Shortage ranking response:', response.data)
      if (response.data.success) {
        setShortageRankingData(response.data.data)
        console.log('[DEBUG] Shortage ranking data set:', response.data.data)
      }
    } catch (error) {
      console.error('不足ランキングデータ取得エラー:', error)
    }
  }

  // ギャップ分析データの取得
  const loadGapAnalysis = async () => {
    try {
      setGapAnalysisError(null)  // エラー状態をリセット
      const params: any = {}

      if (gapAnalysisTarget === 'department') {
        // 特定の部門が選択されている場合のみdepartmentIdを設定
        if (selectedDepartment && selectedDepartment !== 'all') {
          params.departmentId = selectedDepartment
        }
        // 'all'の場合はdepartmentIdを設定せず、全技術部門を対象とする
      }

      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle
      params.targetLevel = gapTargetLevel

      console.log('[DEBUG] Gap Analysis - Start loading')
      console.log('[DEBUG] Gap Analysis - Params:', params)
      console.log('[DEBUG] Gap Analysis - gapAnalysisTarget:', gapAnalysisTarget)
      console.log('[DEBUG] Gap Analysis - selectedDepartment:', selectedDepartment)

      const response = await api.get('/skills/skill-map/gap-analysis', {
        params
      })

      console.log('[DEBUG] Gap analysis response:', response.data)
      if (response.data.success) {
        setGapAnalysisData(response.data.data)
        console.log('[DEBUG] Gap analysis data set:', response.data.data)
        console.log('[DEBUG] Gap analysis gaps array:', response.data.data.gaps)
      } else {
        setGapAnalysisError(response.data.error || 'データ取得に失敗しました')
        setGapAnalysisData(null)
      }
    } catch (error) {
      console.error('ギャップ分析データ取得エラー:', error)
      setGapAnalysisError(extractErrorMessage(error, 'エラーが発生しました'))
      setGapAnalysisData(null)
    }
  }

  // カテゴリ別平均スコア推移の読み込み
  const loadCategoryTrend = async () => {
    try {
      const params: any = {
        months: categoryTrendMonths
      }

      // 部門フィルター（全部門の場合は指定しない）
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      console.log('[Category Trend] リクエストパラメータ:', params)

      const response = await api.get('/skills/skill-map/category-trend', { params })

      console.log('[Category Trend] レスポンス:', response.data)

      if (response.data.success) {
        setCategoryTrendData(response.data.data)
      } else {
        console.error('[Category Trend] エラー:', response.data.error)
        setCategoryTrendData(null)
      }
    } catch (error) {
      console.error('カテゴリ推移データ取得エラー:', error)
      setCategoryTrendData(null)
    }
  }

  // スキル検索の実行
  const handleSkillSearch = async () => {
    if (selectedSkills.length === 0) {
      return
    }

    try {
      const params: any = {
        skillIds: selectedSkills.join(','),
        matchMode: searchMatchMode
      }

      if (searchMinLevel) {
        params.minLevel = searchMinLevel
      }

      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      const response = await api.get('/skills/skill-map/search', {
        params
      })

      if (response.data.success) {
        setSkillSearchResults(response.data.data)
      }
    } catch (error) {
      console.error('スキル検索エラー:', error)
    }
  }

  // ============================================================================
  // 新しいスキルマップ可視化データ取得関数（成果物10工程細分化対応）
  // ============================================================================

  // 真のヒートマップデータ取得
  const loadTrueHeatmap = async () => {
    try {
      setSkillMapLoading(true)
      const params: any = {}

      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }

      if (selectedCategory && selectedCategory !== 'all') {
        params.categoryId = selectedCategory
      }

      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      console.log('[DEBUG] Loading true heatmap with params:', params)
      const response = await api.get('/skills/skill-map/true-heatmap', {
        params
      })

      console.log('[DEBUG] True heatmap response:', response.data)
      if (response.data.success) {
        setTrueHeatmapData(response.data.data)
        console.log('[DEBUG] True heatmap data set:', response.data.data)
      }
    } catch (error) {
      console.error('真のヒートマップデータ取得エラー:', error)
    } finally {
      setSkillMapLoading(false)
    }
  }

  // カテゴリ分布データ取得（拡張版）
  const loadCategoryDistributionEnhanced = async () => {
    try {
      setSkillMapLoading(true)
      const params: any = {}

      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      console.log('[DEBUG] Loading category distribution enhanced with params:', params)
      const response = await api.get('/skills/skill-map/category-distribution-enhanced', {
        params
      })

      console.log('[DEBUG] Category distribution enhanced response:', response.data)
      if (response.data.success) {
        setCategoryDistData(response.data.data)
        console.log('[DEBUG] Category distribution data set:', response.data.data)
      }
    } catch (error) {
      console.error('カテゴリ分布データ取得エラー:', error)
    } finally {
      setSkillMapLoading(false)
    }
  }

  // スキル推移データ取得
  const loadTrendAnalysis = async () => {
    try {
      setSkillMapLoading(true)
      const params: any = {
        months: trendMonths
      }

      if (selectedDepartment && selectedDepartment !== 'all') {
        params.departmentId = selectedDepartment
      }
      if (selectedJobTitle && selectedJobTitle !== 'all') params.jobTitle = selectedJobTitle

      console.log('[DEBUG] Loading trend analysis with params:', params)
      const response = await api.get('/skills/skill-map/trend-analysis', {
        params
      })

      console.log('[DEBUG] Trend analysis response:', response.data)
      if (response.data.success) {
        setTrendData(response.data.data)
        console.log('[DEBUG] Trend analysis data set:', response.data.data)
      }
    } catch (error) {
      console.error('スキル推移データ取得エラー:', error)
    } finally {
      setSkillMapLoading(false)
    }
  }

  // 従業員履歴書データ取得
  const loadEmployeeResumeData = async (employeeId: string) => {
    try {
      setResumeLoading(true)
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/50e25d17-a07f-45b6-9dda-cc012fe14890',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ReportPage.tsx:loadEmployeeResumeData',message:'API call start',data:{employeeId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'B,D'})}).catch(()=>{});
      // #endregion
      
      // 単一のAPIで全データを取得
      const response = await api.get(`/skills/skill-map/employee-resume/${employeeId}`)
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/50e25d17-a07f-45b6-9dda-cc012fe14890',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ReportPage.tsx:responses',message:'API response',data:{success:response.data?.success,skillsCount:response.data?.skills?.length||0,projectsCount:response.data?.projects?.length||0,certsCount:response.data?.certifications?.length||0},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      
      setEmployeeResumeData({
        skills: response.data.skills || [],
        projects: response.data.projects || [],
        certifications: response.data.certifications || []
      })
    } catch (error) {
      console.error('従業員履歴書データ取得エラー:', error)
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/50e25d17-a07f-45b6-9dda-cc012fe14890',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ReportPage.tsx:catch',message:'General error',data:{error:(error as any)?.message,status:(error as any)?.response?.status},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      setEmployeeResumeData({ skills: [], projects: [], certifications: [] })
    } finally {
      setResumeLoading(false)
    }
  }

  // 履歴書ダイアログが開いたときにデータを取得
  useEffect(() => {
    if (resumeDialogOpen && selectedEmployee?.id) {
      loadEmployeeResumeData(selectedEmployee.id)
    }
  }, [resumeDialogOpen, selectedEmployee])

  // スキルマップタブ選択時とフィルタ変更時のデータ取得
  useEffect(() => {
    if (currentTabKey === 'skill-map') {
      // 初回読み込み（部門・カテゴリリストも取得）
      if (departments.length === 0 && categories.length === 0) {
        loadSkillMapData()
      } else {
        // フィルタ変更時は統計とマトリクスのみ更新
        loadSkillMapMatrix()
        loadSkillMapStats()
        loadCategoryDistribution()
        loadSkillProgress()
        loadSkillRadarData()
        loadSkillHeatmapData()
        loadShortageRanking()
        loadGapAnalysis()
        loadCategoryTrend()
        // 新しいスキルマップ可視化データも取得
        loadTrueHeatmap()
        loadCategoryDistributionEnhanced()
        loadTrendAnalysis()
      }
    }
  }, [selectedDepartment, selectedCategory, selectedJobTitle, currentTabKey, trendMonths, categoryTrendMonths, displayLimit])

  // タブ切り替え時のデータ取得
  useEffect(() => {
    if (currentTabKey === 'evaluation-progress') {
      // 評価進捗タブ
      if (selectedPeriod) {
        loadMyEvaluation()
        loadEvaluatorData(selectedPeriod)
        loadOrgProgress(selectedPeriod)
      }
    } else if (currentTabKey === 'performance-trends') {
      // パフォーマンス推移タブ
      loadPerformanceTrends()
    }
  }, [currentTabKey, selectedPeriod])

  // 初回マウント時に評価期間を取得
  useEffect(() => {
    loadEvaluationPeriods()
  }, [])

  // カテゴリ推移グラフ用のデータ変換
  const categoryTrendChartData = useMemo(() => {
    if (!categoryTrendData || !categoryTrendData.categories || categoryTrendData.categories.length === 0) {
      return []
    }

    // 全ての月を収集
    const monthsSet = new Set<string>()
    categoryTrendData.categories.forEach((cat: any) => {
      cat.timeSeries.forEach((ts: any) => monthsSet.add(ts.month))
    })

    const sortedMonths = Array.from(monthsSet).sort()

    // 月ごとにカテゴリデータを集約
    return sortedMonths.map(month => {
      const point: any = { month }
      categoryTrendData.categories.forEach((cat: any) => {
        const data = cat.timeSeries.find((ts: any) => ts.month === month)
        point[cat.categoryId] = data ? data.avgScore : null
      })
      return point
    })
  }, [categoryTrendData])

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return formatDateJST(dateString)
  }

  const formatNumber = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '-'
    }
    return Number(value).toFixed(decimals)
  }

  // 社員の折り畳み状態を切り替える
  const toggleEmployeeExpanded = (employeeId: string) => {
    const newExpanded = new Set(expandedEmployees)
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId)
    } else {
      newExpanded.add(employeeId)
    }
    setExpandedEmployees(newExpanded)
  }

  // スキルレベルの色を取得
  const getSkillLevelColor = (level: string): string => {
    const colorMap: { [key: string]: string } = {
      'S': '#9c27b0', // 紫
      'A': '#2196f3', // 青
      'B': '#4caf50', // 緑
      'C': '#ff9800', // オレンジ
      'D': '#9e9e9e', // グレー
    }
    return colorMap[level] || '#e0e0e0'
  }

  // プログレスバーコンポーネント
  const EvaluationProgressBar: React.FC<{
    status: string;
    goalApprovalStatus?: string | null;
  }> = ({ status, goalApprovalStatus }) => {
    const activeStep = getProgressStep(status as EvaluationStatus, goalApprovalStatus)

    return (
      <Box sx={{ width: '100%', mt: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {PROGRESS_STEPS.map((label, index) => (
            <Step key={index} completed={index < activeStep}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.75rem',
                    mt: 0.5
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          レポート
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="レポートタブ">
            {visibleTabs.map((tab) => (
              <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} />
            ))}
          </Tabs>
        </Box>

              {/* 営業ダッシュボード */}
              {currentTabKey === 'sales-dashboard' && (
                <Box sx={{ p: 3 }}>
                  <SalesDashboardPage />
                </Box>
              )}

              {/* 評価進捗 */}
              {currentTabKey === 'evaluation-progress' && (
                <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>評価期間</InputLabel>
                  <Select
                    value={selectedPeriod}
                    label="評価期間"
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    {availablePeriods.map((period) => (
                      <MenuItem key={period.period_id} value={period.period_id}>
                        {period.period_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* HR向け：全社進捗ファネル＋滞留サマリ＋部門別進捗マトリクス */}
          {user?.roles?.includes('hr') && orgProgress?.funnel && (() => {
            const f = orgProgress.funnel
            const total = Number(f.total) || 0
            // 段階の定義（名称・色）は statusLabels.ts を正本とする
            const funnelStages = EVALUATION_FUNNEL_STAGES.map((stage) => ({
              ...stage,
              value: getFunnelStageCount(f, stage.key),
            }))
            const matrix = orgProgress.departmentMatrix || []
            const stall = orgProgress.stallSummary || { thresholdDays: 7, totalStalled: 0, topStage: null, byStage: [] }
            const owners = orgProgress.nextActionOwners || { evaluators: [], selfPending: 0, hrPending: 0 }
            const ownerStageLabel = (s: string) => (s === 'manager' ? '上長' : s === 'dept_head' ? '部門長' : s)
            return (
              <Card sx={{ mb: 3 }} variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon color="primary" />
                    全社評価進捗（HR向け）
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={3}>
                    {/* 左：全社ファネル＋ボトルネックサマリ */}
                    <Grid item xs={12} md={5}>
                      <Typography variant="subtitle1" gutterBottom>
                        全社進捗ファネル（段階別人数） 総対象者数 {total}人
                      </Typography>
                      {funnelStages.map((stage) => (
                        <Box key={stage.key} sx={{ mb: 1.25 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">{stage.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stage.value}人（{total > 0 ? ((stage.value / total) * 100).toFixed(1) : '0.0'}%）
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 14 }}>
                              <Box
                              sx={{
                                width: `${total > 0 ? (stage.value / total) * 100 : 0}%`,
                                bgcolor: stage.color,
                                height: 14,
                                borderRadius: 1,
                                transition: 'width 0.3s'
                              }}
                            />
                          </Box>
                        </Box>
                      ))}

                      {/* ボトルネック・サマリ */}
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          ボトルネック（{stall.thresholdDays}日以上 動きなし）
                        </Typography>
                        {stall.topStage ? (
                          <Typography variant="body2" color="text.secondary">
                            最も滞留: <strong>{stall.topStage.label}</strong> {stall.topStage.stalled}人
                            {stall.topStage.avgDays != null && `（平均 ${stall.topStage.avgDays.toFixed(1)}日）`}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">現在、しきい値超の滞留はありません</Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          停滞合計: <strong>{stall.totalStalled}人</strong>
                        </Typography>
                        {/* 段階別 滞留内訳 */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                          {(stall.byStage || []).filter((s: any) => s.stalled > 0).map((s: any) => (
                            <Chip
                              key={s.key}
                              size="small"
                              variant="outlined"
                              color="warning"
                              label={`${s.label} ${s.stalled}人${s.avgDays != null ? `/${s.avgDays.toFixed(0)}日` : ''}`}
                            />
                          ))}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          次アクション者（未処理 件数）
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {owners.selfPending > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">被評価者本人（自己評価待ち）</Typography>
                              <Typography variant="body2"><strong>{owners.selfPending}</strong>人</Typography>
                            </Box>
                          )}
                          {(owners.evaluators || []).map((ev: any) => (
                            <Box key={`${ev.evaluator_id}-${ev.stage}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                {ev.evaluator_name}
                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                  （{ownerStageLabel(ev.stage)}評価待ち）
                                </Typography>
                              </Typography>
                              <Typography variant="body2"><strong>{ev.pending_count}</strong>件</Typography>
                            </Box>
                          ))}
                          {owners.hrPending > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">HR（会社業績調整・確定待ち）</Typography>
                              <Typography variant="body2"><strong>{owners.hrPending}</strong>人</Typography>
                            </Box>
                          )}
                          {owners.selfPending === 0 && owners.hrPending === 0 && (owners.evaluators || []).length === 0 && (
                            <Typography variant="body2" color="text.secondary">未処理はありません</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* 右：部門別 進捗マトリクス */}
                    <Grid item xs={12} md={7}>
                      <Typography variant="subtitle1" gutterBottom>
                        部門別 進捗（遅れている部門が上）
                      </Typography>
                      {/* 凡例 */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {EVALUATION_FUNNEL_STAGES.map((s) => (
                          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color }} />
                            <Typography variant="caption" color="text.secondary">{s.shortLabel}</Typography>
                          </Box>
                        ))}
                      </Box>
                      {matrix.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">データがありません</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {matrix.map((dept: any) => {
                            const dTotal = Number(dept.total) || 0
                            return (
                              <Box key={dept.department_name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* 部門名＋滞留 */}
                                <Box sx={{ width: 140, flexShrink: 0 }}>
                                  <Typography variant="body2" noWrap title={dept.department_name}>
                                    {dept.department_name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.disabled">{dTotal}人</Typography>
                                    {dept.stalled_count > 0 && (
                                      <Chip size="small" color="warning" variant="outlined" label={`滞留${dept.stalled_count}`} sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                    )}
                                  </Box>
                                </Box>
                                {/* 積み上げバー */}
                                <Box sx={{ flexGrow: 1, display: 'flex', height: 18, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}>
                                  {EVALUATION_FUNNEL_STAGES.map((s) => {
                                    const v = getFunnelStageCount(dept.stages, s.key)
                                    if (v <= 0) return null
                                    const w = dTotal > 0 ? (v / dTotal) * 100 : 0
                                    return (
                                      <Tooltip key={s.key} title={`${s.label}: ${v}人`}>
                                        <Box sx={{ width: `${w}%`, bgcolor: s.color, height: '100%' }} />
                                      </Tooltip>
                                    )
                                  })}
                                </Box>
                                {/* 進捗指数＋完了率 */}
                                <Box sx={{ width: 92, flexShrink: 0, textAlign: 'right' }}>
                                  <Typography variant="body2"><strong>{Number(dept.progress_index).toFixed(1)}%</strong></Typography>
                                  <Typography variant="caption" color="text.disabled">確定 {Number(dept.completion_rate).toFixed(0)}%</Typography>
                                </Box>
                              </Box>
                            )
                          })}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        進捗指数 = 段階の加重平均（未着手0〜確定100%）。バーは各段階の人数構成。
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )
          })()}

          {loading && !myEvaluationData && !evaluatorAssignments.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Section A: 自分の評価 */}
              {myEvaluationData && myEvaluationData.evaluation && (
                <Card sx={{ mb: 3 }} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon color="primary" />
                        あなたの評価
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowMyDetail(true)}
                        startIcon={<VisibilityIcon />}
                      >
                        詳細を表示
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">社員番号</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.employee?.employee_id || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">氏名</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.employee?.name || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">部門</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.employee?.department_name || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">職級</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.employee?.job_grade || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">ランク</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.employee?.rank || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">評価期間</Typography>
                        <Typography variant="body1" gutterBottom>{myEvaluationData.evaluation?.period_name || myEvaluationData.evaluation?.evaluation_period || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>評価進捗</Typography>
                        <EvaluationProgressBar
                          status={myEvaluationData.evaluation?.status || 'draft'}
                          goalApprovalStatus={myEvaluationData.goal_approval_status}
                        />
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                          <Typography variant="h3" color="primary.contrastText">
                            {formatNumber(myEvaluationData.evaluation?.performance_score)}
                          </Typography>
                          <Typography variant="caption" color="primary.contrastText">
                            パフォーマンス評価点
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                          {canViewSalaryRate(
                            myEvaluationData.evaluation?.status as EvaluationStatus,
                            user?.role || 'employee',
                            false
                          ) ? (
                            <>
                              <Typography variant="h3" color="secondary.contrastText">
                                {formatNumber(myEvaluationData.evaluation?.salary_increase_rate)}%
                              </Typography>
                              <Typography variant="caption" color="secondary.contrastText">
                                昇給率
                              </Typography>
                            </>
                          ) : (
                            <Tooltip title="昇給率は評価確定後に表示されます">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <VisibilityOffIcon sx={{ fontSize: 48, color: 'secondary.contrastText', opacity: 0.5 }} />
                                <Typography variant="caption" color="secondary.contrastText">
                                  昇給率（非表示）
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Section B: 担当している評価 */}
              {evaluatorAssignments.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    担当している評価
                  </Typography>

                  {/* Analytics Summary Card */}
                  {evaluatorAnalytics && (
                    <Card sx={{ mb: 3 }} variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          統計サマリー
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="primary">
                                {evaluatorAnalytics.totalCount}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                担当人数
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" color="info.main">
                                {formatNumber(evaluatorAnalytics.performanceScore?.avg)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                平均P点数
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                (評価済み {evaluatorAnalytics.performanceScore?.scoredCount ?? 0} / {evaluatorAnalytics.totalCount}人)
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                (Min: {formatNumber(evaluatorAnalytics.performanceScore?.min)},
                                Max: {formatNumber(evaluatorAnalytics.performanceScore?.max)})
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              {/* 昇給率は評価確定後のみ表示 */}
                              {user?.roles?.includes('hr') || evaluatorAssignments.every((assignment: any) =>
                                assignment.status === 'finalized'
                              ) ? (
                                <>
                                  <Typography variant="h5" color="success.main">
                                    {formatNumber(evaluatorAnalytics.salaryIncreaseRate?.avg)}%
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    平均昇給率
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    (Min: {formatNumber(evaluatorAnalytics.salaryIncreaseRate?.min)}%,
                                    Max: {formatNumber(evaluatorAnalytics.salaryIncreaseRate?.max)}%)
                                  </Typography>
                                </>
                              ) : (
                                <Tooltip title="昇給率は全ての評価が確定後に表示されます">
                                  <Box>
                                    <VisibilityOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="caption" color="text.disabled" display="block">
                                      平均昇給率
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" display="block">
                                      (非表示)
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" gutterBottom>
                                ステータス分布
                              </Typography>
                              {(() => {
                                // goal_setting は承認状態でラベルが分かれるため、同一ラベルを集約
                                const aggregated = new Map<
                                  string,
                                  { label: string; chipColor: any; count: number }
                                >()
                                ;(evaluatorAnalytics.statusDistribution || []).forEach((stat: any) => {
                                  const statusInfo = getStatusLabel(
                                    stat.status as EvaluationStatus,
                                    stat.goal_approval_status as GoalApprovalStatus
                                  )
                                  const existing = aggregated.get(statusInfo.label)
                                  if (existing) {
                                    existing.count += stat.count
                                  } else {
                                    aggregated.set(statusInfo.label, {
                                      label: statusInfo.label,
                                      chipColor: statusInfo.chipColor,
                                      count: stat.count,
                                    })
                                  }
                                })
                                return Array.from(aggregated.values()).map((item) => (
                                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Chip label={item.label} color={item.chipColor} size="small" />
                                    <Typography variant="caption">{item.count}人</Typography>
                                  </Box>
                                ))
                              })()}
                            </Box>
                          </Grid>
                        </Grid>
                        {evaluatorAnalytics.departmentDistribution && evaluatorAnalytics.departmentDistribution.length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              部門別分布
                            </Typography>
                            <Grid container spacing={1}>
                              {evaluatorAnalytics.departmentDistribution.map((dept: any) => (
                                <Grid item xs={12} sm={6} md={4} key={dept.department_name}>
                                  <Paper variant="outlined" sx={{ p: 1 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {dept.department_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {dept.count}人 | 平均P: {formatNumber(dept.avg_performance_score)}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Evaluator Assignments Table */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>社員ID</TableCell>
                          <TableCell>氏名</TableCell>
                          <TableCell>部門</TableCell>
                          <TableCell>職級</TableCell>
                          <TableCell>ランク</TableCell>
                          <TableCell>評価進捗</TableCell>
                          <TableCell align="right">P点数</TableCell>
                          <TableCell align="right">昇給率 (%)</TableCell>
                          <TableCell align="center">操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {evaluatorAssignments.map((assignment: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{assignment.employee_id}</TableCell>
                            <TableCell>{assignment.name}</TableCell>
                            <TableCell>{assignment.department_name}</TableCell>
                            <TableCell>{assignment.job_grade}</TableCell>
                            <TableCell>{assignment.rank}</TableCell>
                            <TableCell>
                              <EvaluationProgressBar
                                status={assignment.status || 'draft'}
                                goalApprovalStatus={assignment.goal_approval_status}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(assignment.performance_score)}
                            </TableCell>
                            <TableCell align="right">
                              {canViewSalaryRate(
                                assignment.status as EvaluationStatus,
                                user?.role || 'employee',
                                true // 評価者として見ている
                              ) ? (
                                formatNumber(assignment.salary_increase_rate)
                              ) : (
                                <Tooltip title="昇給率は評価確定後に表示されます">
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <VisibilityOffIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.disabled">非表示</Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => loadEmployeeDetail(assignment.employee_id, selectedPeriod)}
                                  title="評価詳細を表示"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => loadEmployeeHistory(assignment.employee_id, assignment.name)}
                                  title="評価履歴を表示"
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Empty State */}
              {!myEvaluationData && evaluatorAssignments.length === 0 && !loading && (
                <Alert severity="info">
                  評価データがありません。評価期間を選択してください。
                </Alert>
              )}
            </>
          )}
                </Box>
              )}

              {/* パフォーマンス推移 */}
              {currentTabKey === 'performance-trends' && (
                <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              あなたの過去全ての評価履歴を表示しています
            </Typography>
          </Box>

          {loading && trendsData.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {trendsData.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>評価期間</TableCell>
                    <TableCell>氏名</TableCell>
                    <TableCell align="right">パフォーマンス評価点</TableCell>
                    <TableCell align="right">昇給率 (%)</TableCell>
                    <TableCell>更新日</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trendsData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.evaluation_period}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{formatNumber(row.performance_score)}</TableCell>
                      <TableCell align="right">
                        {canViewSalaryRate(
                          row.status as EvaluationStatus,
                          user?.role || 'employee',
                          true // 評価者として見ている
                        ) ? (
                          formatNumber(row.salary_increase_rate)
                        ) : (
                          <Tooltip title="昇給率は評価確定後に表示されます">
                            <Box component="span" sx={{ color: 'text.disabled' }}>非表示</Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(row.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && trendsData.length === 0 && (
            <Alert severity="info">データがありません</Alert>
          )}
                </Box>
              )}

              {/* 昇給率シミュレーション */}
              {currentTabKey === 'salary-simulation' && (
                <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    入力パラメータ
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <TextField
                    fullWidth
                    margin="normal"
                    label="パフォーマンス評価点 (P)"
                    type="number"
                    value={simulationParams.performanceScore}
                    onChange={(e) =>
                      setSimulationParams({
                        ...simulationParams,
                        performanceScore: Number(e.target.value),
                      })
                    }
                    inputProps={{ min: 1, max: 10, step: 0.1 }}
                    helperText="1～10点（基準点: 6）"
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    label="勤続年数 (Y)"
                    type="number"
                    value={simulationParams.yearsOfService}
                    onChange={(e) =>
                      setSimulationParams({
                        ...simulationParams,
                        yearsOfService: Number(e.target.value),
                      })
                    }
                    inputProps={{ min: 0, max: 50 }}
                    helperText="年"
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    label="会社業績調整率 (R)"
                    type="number"
                    value={simulationParams.companyAdjustmentRate}
                    onChange={(e) =>
                      setSimulationParams({
                        ...simulationParams,
                        companyAdjustmentRate: Number(e.target.value),
                      })
                    }
                    inputProps={{ step: 0.1 }}
                    helperText="%"
                  />

                  <Box sx={{ mt: 3 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={runSalarySimulation}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'シミュレーション実行'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {simulationResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      計算結果
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h4" color="primary" align="center">
                        {simulationResult.calculatedIncreaseRate?.toFixed(2)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        昇給率
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                      計算内訳
                    </Typography>

                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        パフォーマンス成分:{' '}
                        {simulationResult.breakdown?.performanceComponent?.toFixed(2)}%
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (2.0 × (P - 6))
                        </Typography>
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        勤続年数成分:{' '}
                        {simulationResult.breakdown?.serviceYearsComponent?.toFixed(2)}%
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (0.05 × Y)
                        </Typography>
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        会社業績調整成分:{' '}
                        {simulationResult.breakdown?.companyAdjustmentComponent?.toFixed(2)}%
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (0.30 × R)
                        </Typography>
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">
                      計算式: 昇給率 (%) = (2.0 × (P - 6)) + (0.05 × Y) + (0.30 × R)
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {!simulationResult && (
                <Card variant="outlined">
                  <CardContent>
                    <Alert severity="info">
                      パラメータを入力してシミュレーションを実行してください
                    </Alert>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
                </Box>
              )}

              {/* スキルマップ可視化・分析 */}
              {currentTabKey === 'skill-map' && (
                <Box sx={{ p: 3 }}>
            {skillMapLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!skillMapLoading && skillMapStats && (
              <>
                {/* フィルタと統計サマリー */}
                <Grid container spacing={3} sx={{ mb: 4, alignItems: 'center' }}>
                  {/* 部門選択 */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>部門</InputLabel>
                      <Select
                        value={selectedDepartment}
                        label="部門"
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                      >
                        <MenuItem value="all">全部門</MenuItem>
                        {departments
                          .filter((dept: any) => dept.department_type === 'tech')
                          .map((dept: any) => (
                            <MenuItem key={dept.department_id} value={dept.department_id}>
                              {dept.department_name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 役職選択 */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>役職</InputLabel>
                      <Select
                        value={selectedJobTitle}
                        label="役職"
                        onChange={(e) => setSelectedJobTitle(e.target.value)}
                      >
                        <MenuItem value="all">全て</MenuItem>
                        {jobTitles.map((title: string) => (
                          <MenuItem key={title} value={title}>
                            {title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 統計カード */}
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
                          <CardContent sx={{ py: 1, px: 2, width: '100%', '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                対象社員数
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {skillMapStats.registrationRate?.totalEmployees || 0} 名
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
                          <CardContent sx={{ py: 1, px: 2, width: '100%', '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                スキル登録済み社員
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {skillMapStats.registrationRate?.employeesWithSkills || 0} 名
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
                          <CardContent sx={{ py: 1, px: 2, width: '100%', '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                スキル登録率
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {skillMapStats.registrationRate?.rate || 0}%
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* カテゴリ別平均スコアとレーダーチャート（左右配置） */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* カテゴリ別平均スコア */}
                  <Grid item xs={12} md={6}>
                    {skillMapStats.categoryStats && skillMapStats.categoryStats.length > 0 && (
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            カテゴリ別平均スコア（人単位）
                          </Typography>
                          <Grid container spacing={2}>
                            {skillMapStats.categoryStats.map((category: any) => (
                              <Grid item xs={12} sm={6} key={category.category_id}>
                                <Card variant="outlined">
                                  <CardContent sx={{ padding: '8px 12px !important' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2">
                                        {category.category_name}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                        <Typography variant="h6" sx={{
                                          fontWeight: 'bold',
                                          color: (() => {
                                            const score = parseFloat(category.category_avg_score);
                                            if (score > 4) return '#9c27b0'; // S級
                                            if (score > 3) return '#2196f3'; // A級
                                            if (score > 2) return '#4caf50'; // B級
                                            if (score > 1) return '#ff9800'; // C級
                                            return '#9e9e9e'; // D級
                                          })()
                                        }}>
                                          {category.category_avg_score}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'normal' }}>
                                          /5.0
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>

                  {/* カテゴリ別スキルレーダーチャート */}
                  <Grid item xs={12} md={6}>
                    {skillRadarData && skillRadarData.categories && skillRadarData.categories.length > 0 && (
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            カテゴリ別スキルバランス（レーダーチャート）
                          </Typography>
                          <ResponsiveContainer width="100%" height={400}>
                            <RadarChart data={skillRadarData.categories}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="category" />
                              <PolarRadiusAxis domain={[0, skillRadarData.maxScore]} />
                              <RechartsTooltip />
                              <Legend />
                              <Radar
                                name="全体"
                                dataKey="全体"
                                stroke="#2196f3"
                                fill="#2196f3"
                                fillOpacity={0.3}
                              />
                              {selectedDepartment && selectedDepartment !== 'all' && (
                                <Radar
                                  name={departments.find((d: any) => d.department_id === selectedDepartment)?.department_name || '選択部門'}
                                  dataKey={departments.find((d: any) => d.department_id === selectedDepartment)?.department_name || '選択部門'}
                                  stroke="#ff9800"
                                  fill="#ff9800"
                                  fillOpacity={0.3}
                                />
                              )}
                            </RadarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>

                {/* ===== 新規: スキルレベル分布ヒートマップ（色濃度表示） ===== */}
                {categoryDistData && categoryDistData.categories && categoryDistData.categories.length > 0 && (() => {
                  // 全カテゴリのS/A/B/C/D人数から最大値を計算
                  const maxCount = Math.max(...categoryDistData.categories.flatMap(cat =>
                    ['S', 'A', 'B', 'C', 'D'].map(level => cat.distribution[level as keyof typeof cat.distribution].count)
                  ))

                  // 赤系グラデーション色計算関数（改善版：より滑らかなグラデーション）
                  const getRedHeatColor = (count: number) => {
                    if (count === 0) return 'rgb(255, 248, 248)' // ほぼ白（より明るく）

                    // 段階的なグラデーションを実現するための非線形変換
                    const intensity = Math.pow(count / maxCount, 0.7) // 0.7乗で中間色を豊かに

                    // より滑らかな色の遷移：薄いピンク → 濃い赤
                    // 薄い: rgb(255, 235, 235)
                    // 中間: rgb(255, 140, 140)
                    // 濃い: rgb(180, 30, 30)
                    const r = Math.round(255 - 75 * intensity)
                    const g = Math.round(235 - 205 * intensity)
                    const b = Math.round(235 - 205 * intensity)

                    return `rgb(${r}, ${g}, ${b})`
                  }

                  return (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        {/* タイトル行（左：タイトル、右：色凡例） */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">
                            スキルレベル分布ヒートマップ
                          </Typography>

                          {/* カラースケール凡例（右寄せ） */}
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mb: 0.5 }}>
                              色の濃さ（赤系グラデーション）
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption">薄い（人数少）</Typography>
                              <Box
                                sx={{
                                  width: 150,
                                  height: 16,
                                  background: 'linear-gradient(to right, rgb(255,248,248), rgb(255,180,180), rgb(255,100,100), rgb(180,30,30))',
                                  borderRadius: 0.5,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Typography variant="caption">濃い（人数多）</Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* ヒートマップテーブル */}
                        <Box sx={{ width: '100%' }}>
                          <TableContainer component={Paper}>
                            <Table
                              size="small"
                              sx={{
                                tableLayout: 'fixed',
                                width: '100%',
                                borderCollapse: 'separate',
                                borderSpacing: '2px',
                                '& th, & td': {
                                  border: 'none'
                                }
                              }}
                            >
                              <colgroup>
                                <col style={{ width: '200px' }} />
                                <col style={{ width: 'calc((100% - 200px) / 5)' }} />
                                <col style={{ width: 'calc((100% - 200px) / 5)' }} />
                                <col style={{ width: 'calc((100% - 200px) / 5)' }} />
                                <col style={{ width: 'calc((100% - 200px) / 5)' }} />
                                <col style={{ width: 'calc((100% - 200px) / 5)' }} />
                              </colgroup>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold',
                                      bgcolor: 'grey.100',
                                      paddingX: 2,
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 3,
                                      borderRadius: 1
                                    }}
                                  >
                                    カテゴリ
                                  </TableCell>
                                  {['S', 'A', 'B', 'C', 'D'].map((level, idx) => (
                                    <TableCell
                                      key={level}
                                      align="center"
                                      sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'grey.50',
                                        borderRadius: 1
                                      }}
                                    >
                                      {level}
                                      <br />
                                      <Typography variant="caption" color="text.secondary">
                                        {['エキスパート', '指導', '独力', '補助', '知識'][idx]}
                                      </Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                            <TableBody>
                              {categoryDistData.categories.map((cat) => {
                                // カテゴリ内の全レベルの合計人数を計算
                                const categoryTotal = ['S', 'A', 'B', 'C', 'D'].reduce((sum, level) => {
                                  return sum + (cat.distribution[level as keyof typeof cat.distribution]?.count || 0)
                                }, 0)

                                return (
                                  <TableRow key={cat.categoryId}>
                                    <TableCell
                                      sx={{
                                        fontWeight: 'medium',
                                        bgcolor: '#fafafa',
                                        paddingX: 2,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        borderRadius: 1
                                      }}
                                    >
                                      {cat.categoryName}
                                    </TableCell>
                                    {['S', 'A', 'B', 'C', 'D'].map((level) => {
                                      const dist = cat.distribution[level as keyof typeof cat.distribution]
                                      const count = dist?.count || 0
                                      // パーセンテージをカテゴリ合計に対する割合として再計算
                                      const percentage = categoryTotal > 0 ? (count / categoryTotal) * 100 : 0
                                      const bgColor = getRedHeatColor(count)

                                      // テキスト色: 濃い背景なら白、薄い背景なら黒
                                      const textColor = count / maxCount > 0.5 ? '#fff' : '#000'

                                      return (
                                        <TableCell
                                          key={level}
                                          align="center"
                                          onClick={() => handleCategoryHeatmapCellClick(Number(cat.categoryId), cat.categoryName, level)}
                                          sx={{
                                            bgcolor: bgColor,
                                            color: textColor,
                                            fontWeight: count > 0 ? 'bold' : 'normal',
                                            height: 40,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                              transform: 'scale(1.05)',
                                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                              zIndex: 10
                                            }
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                                            <Box sx={{ width: '20%' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                              {count}名
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                                              ({percentage.toFixed(1)}%)
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      )
                                    })}
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                          ※ 対象社員数: {categoryDistData.categories[0]?.statistics?.totalEmployees || 0}名 /
                          最大人数: {maxCount}名 /
                          色の濃さは全カテゴリ中の最大人数を100%として赤系グラデーションで表示されます
                        </Typography>
                      </CardContent>
                    </Card>
                  )
                })()}

                {/* スキルマップヒートマップ */}
                {filteredHeatmapData && filteredHeatmapData.employees && filteredHeatmapData.employees.length > 0 && (
                  <Card id="skillmap-heatmap-section" sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          従業員×スキル
                          {(skillFilter || levelFilter) && (
                            <Typography component="span" variant="body2" color="primary" sx={{ ml: 2 }}>
                              （{filteredHeatmapData.employees.length}名表示中）
                            </Typography>
                          )}
                        </Typography>
                        {/* レベル凡例（右寄せ） */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">スキルレベル:</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#9c27b0', border: '1px solid #ddd' }} />
                              <Typography variant="caption">S(5)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#2196f3', border: '1px solid #ddd' }} />
                              <Typography variant="caption">A(4)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', border: '1px solid #ddd' }} />
                              <Typography variant="caption">B(3)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#ff9800', border: '1px solid #ddd' }} />
                              <Typography variant="caption">C(2)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#9e9e9e', border: '1px solid #ddd' }} />
                              <Typography variant="caption">D(1)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: '#f5f5f5', border: '1px solid #ddd' }} />
                              <Typography variant="caption">未登録</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* フィルタ表示エリア */}
                      {(skillFilter || levelFilter || categoryFilter || categoryLevelFilter) && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              フィルタ適用中:
                              {skillFilter && ` スキル="${skillHeatmapData.skills.find((s: any) => s.id === skillFilter)?.name}"`}
                              {levelFilter !== null && ` レベル="${['未登録','D','C','B','A','S'][levelFilter]}"`}
                              {categoryFilter && categoryDistData && ` カテゴリ="${categoryDistData.categories.find((c: any) => c.categoryId === categoryFilter)?.categoryName}"`}
                              {categoryLevelFilter && ` レベル="${categoryLevelFilter}"`}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleResetHeatmapFilter}
                          >
                            フィルタ解除
                          </Button>
                        </Box>
                      )}

                      <Box sx={{ overflowX: 'auto', mb: 2 }}>
                        <TableContainer component={Paper} sx={{ maxHeight: 900 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    minWidth: 150,
                                    fontWeight: 'bold',
                                    bgcolor: '#f5f5f5',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 3
                                  }}
                                >
                                  従業員
                                </TableCell>
                                {filteredHeatmapData.skills.map((skill: any) => (
                                  <TableCell
                                    key={skill.id}
                                    align="center"
                                    sx={{
                                      minWidth: 60,
                                      fontSize: '0.75rem',
                                      bgcolor: '#f5f5f5',
                                      writingMode: 'vertical-rl',
                                      textOrientation: 'mixed',
                                      whiteSpace: 'nowrap',
                                      padding: '8px 4px'
                                    }}
                                  >
                                    {skill.name}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredHeatmapData.employees.map((employee: any, empIndex: number) => (
                                <TableRow key={employee.id} hover>
                                  <TableCell
                                    onClick={() => {
                                      setSelectedEmployee(employee)
                                      setResumeDialogOpen(true)
                                    }}
                                    sx={{
                                      fontWeight: 'medium',
                                      bgcolor: '#fafafa',
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 2,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: '#e3f2fd'
                                      }
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>{employee.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {employee.department}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  {filteredHeatmapData.matrix[empIndex].map((score: number, skillIndex: number) => {
                                    const getScoreColor = (s: number) => {
                                      if (s === 5) return '#9c27b0'; // S
                                      if (s === 4) return '#2196f3'; // A
                                      if (s === 3) return '#4caf50'; // B
                                      if (s === 2) return '#ff9800'; // C
                                      if (s === 1) return '#9e9e9e'; // D
                                      return '#f5f5f5'; // 未登録
                                    };

                                    const getScoreLabel = (s: number) => {
                                      if (s === 5) return 'S';
                                      if (s === 4) return 'A';
                                      if (s === 3) return 'B';
                                      if (s === 2) return 'C';
                                      if (s === 1) return 'D';
                                      return '-';
                                    };

                                    return (
                                      <TableCell
                                        key={skillIndex}
                                        align="center"
                                        sx={{
                                          bgcolor: getScoreColor(score),
                                          color: score === 0 ? '#999' : '#fff',
                                          fontWeight: score > 0 ? 'bold' : 'normal',
                                          fontSize: '0.75rem',
                                          padding: '4px',
                                          border: '1px solid #ddd'
                                        }}
                                      >
                                        {getScoreLabel(score)}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>

                      {/* ページネーション（さらに表示） */}
                      {skillHeatmapData.employees.length >= displayLimit && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setDisplayLimit(prev => prev + 50)}
                            sx={{ mr: 2 }}
                          >
                            さらに50名表示
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setDisplayLimit(9999)}
                          >
                            全員表示
                          </Button>
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        ※ 表示する従業員数は初期表示50名（スキル保有数の多い順）。セルをクリックすると該当スキル保有者で絞り込みます。
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* ===== 新規: カテゴリ分布（統計情報付き） ===== */}
                {categoryDistData && categoryDistData.categories && categoryDistData.categories.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        カテゴリ別スキル分布と統計情報
                      </Typography>

                      {/* 統計テーブル */}
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>カテゴリ</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>平均スコア</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>標準偏差</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>中央値</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>最頻値</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>カバー率</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {categoryDistData.categories.map((cat: any) => {
                              const avgScore = Number(cat.statistics?.avgScore || 0)
                              const stdDev = Number(cat.statistics?.stdDev || 0)
                              const median = Number(cat.statistics?.median || 0)
                              const mode = String(cat.statistics?.mode || 'D')
                              const coverage = Number(cat.statistics?.coverage || 0)

                              return (
                              <TableRow key={cat.categoryId} hover>
                                <TableCell>{String(cat.categoryName || '')}</TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={avgScore.toFixed(2)}
                                    size="small"
                                    color={avgScore >= 4 ? 'success' : avgScore >= 3 ? 'primary' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">{stdDev.toFixed(2)}</TableCell>
                                <TableCell align="right">{median.toFixed(2)}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={mode}
                                    size="small"
                                    sx={{
                                      bgcolor: getSkillLevelColor(mode),
                                      color: '#fff',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={coverage}
                                        sx={{ height: 8, borderRadius: 1 }}
                                      />
                                    </Box>
                                    <Typography variant="body2" sx={{ minWidth: 50 }}>
                                      {coverage.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        ※ スコア: S=5, A=4, B=3, C=2, D=1 / カバー率: カテゴリ内スキルを1つ以上持つ社員の割合
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* ===== 新規: スキル推移分析 ===== */}
                {trendData && trendData.categories && trendData.categories.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            スキル推移分析（時系列変化）
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            カテゴリ別の平均スキルレベルの推移とレベルアップ状況
                            {trendData.dateRange && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                期間: {trendData.dateRange.start} 〜 {trendData.dateRange.end}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant={trendMonths === 6 ? 'contained' : 'outlined'}
                            onClick={() => setTrendMonths(6)}
                          >
                            6ヶ月
                          </Button>
                          <Button
                            size="small"
                            variant={trendMonths === 12 ? 'contained' : 'outlined'}
                            onClick={() => setTrendMonths(12)}
                          >
                            12ヶ月
                          </Button>
                          <Button
                            size="small"
                            variant={trendMonths === 24 ? 'contained' : 'outlined'}
                            onClick={() => setTrendMonths(24)}
                          >
                            24ヶ月
                          </Button>
                        </Box>
                      </Box>

                      {/* 折れ線グラフ */}
                      <Box sx={{ mb: 3 }}>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart
                            data={(() => {
                              // 全カテゴリの時系列データをマージ
                              const dateMap = new Map()
                              trendData.categories.forEach((cat: any) => {
                                const timeSeries = cat.timeSeries || []
                                timeSeries.forEach((point: any) => {
                                  const month = String(point.month || '')
                                  if (!dateMap.has(month)) {
                                    dateMap.set(month, { month })
                                  }
                                  const shortName = String(cat.categoryName || '').length > 10
                                    ? String(cat.categoryName).substring(0, 10) + '...'
                                    : String(cat.categoryName || '')
                                  dateMap.get(month)[shortName] = Number(point.avgScore || 0)
                                })
                              })
                              return Array.from(dateMap.values()).sort((a, b) => String(a.month).localeCompare(String(b.month)))
                            })()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis
                              domain={[0, 5]}
                              ticks={[0, 1, 2, 3, 4, 5]}
                              label={{ value: '平均スコア', angle: -90, position: 'insideLeft' }}
                            />
                            <RechartsTooltip />
                            <Legend />
                            {trendData.categories.map((cat: any, idx: number) => {
                              const colors = ['#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336', '#00bcd4', '#795548']
                              const shortName = cat.categoryName.length > 10
                                ? cat.categoryName.substring(0, 10) + '...'
                                : cat.categoryName
                              return (
                                <Line
                                  key={cat.categoryId}
                                  type="monotone"
                                  dataKey={shortName}
                                  stroke={colors[idx % colors.length]}
                                  strokeWidth={2}
                                  dot={{ r: 4 }}
                                  name={cat.categoryName}
                                />
                              )
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* トレンド情報テーブル */}
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>カテゴリ</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>直近変化</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>C→B</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>B→A</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>A→S</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>合計UP</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {trendData.categories.map((cat: any) => {
                              const recentChange = Number(cat.trends?.recentChange || 0)
                              const cToB = Number(cat.trends?.upgrades?.C_to_B || 0)
                              const bToA = Number(cat.trends?.upgrades?.B_to_A || 0)
                              const aToS = Number(cat.trends?.upgrades?.A_to_S || 0)
                              const totalUpgrades = cToB + bToA + aToS

                              return (
                                <TableRow key={cat.categoryId} hover>
                                  <TableCell>{String(cat.categoryName || '')}</TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={recentChange >= 0
                                        ? `+${recentChange.toFixed(2)}`
                                        : recentChange.toFixed(2)
                                      }
                                      size="small"
                                      color={recentChange > 0 ? 'success' : recentChange < 0 ? 'error' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip label={String(cToB)} size="small" />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip label={String(bToA)} size="small" />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip label={String(aToS)} size="small" />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={String(totalUpgrades)}
                                      size="small"
                                      color={totalUpgrades > 10 ? 'success' : totalUpgrades > 5 ? 'primary' : 'default'}
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        ※ レベルアップ数: 期間内にレベルが上がった社員の人数 / 直近変化: 最新月と前月の平均スコア差
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Phase 3: スキル不足ランキング */}
                {shortageRankingData && shortageRankingData.rankings && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        スキル不足ランキング（組織で不足しているスキルTOP10）
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>順位</TableCell>
                              <TableCell>スキル名</TableCell>
                              <TableCell>カテゴリ</TableCell>
                              <TableCell align="right">保有者数</TableCell>
                              <TableCell align="right">熟達者数(B以上)</TableCell>
                              <TableCell align="right">カバー率</TableCell>
                              <TableCell align="right">平均レベル</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {shortageRankingData.rankings.slice(0, 10).map((item: any) => (
                              <TableRow key={item.itemId} hover>
                                <TableCell>{item.rank}</TableCell>
                                <TableCell>{item.itemName}</TableCell>
                                <TableCell>{item.category}</TableCell>
                                <TableCell align="right">{item.holderCount} / {item.totalEmployees}</TableCell>
                                <TableCell align="right">{item.proficientCount}</TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={item.coverageRate}
                                        sx={{ height: 8, borderRadius: 1 }}
                                      />
                                    </Box>
                                    <Typography variant="body2">{item.coverageRate}%</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{item.avgScore}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                )}

                {/* デバッグカードとギャップ分析をコメントアウト（将来的な復活のため保持）
                <Card sx={{ mb: 3, bgcolor: '#fff3cd', border: '2px solid #ffc107' }}>
                  ...
                </Card>
                */}

                {/* カテゴリ別平均スコア推移 - コメントアウト（スキル推移分析と重複） */}
                {/*
                {categoryTrendData && categoryTrendData.categories && categoryTrendData.categories.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          カテゴリ別平均スコア推移
                          {categoryTrendData.department
                            ? ` - ${categoryTrendData.department.departmentName}`
                            : ' - 全技術部門'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            期間:
                          </Typography>
                          <Select
                            size="small"
                            value={categoryTrendMonths}
                            onChange={(e) => {
                              setCategoryTrendMonths(Number(e.target.value))
                            }}
                          >
                            <MenuItem value={6}>過去6ヶ月</MenuItem>
                            <MenuItem value={12}>過去12ヶ月</MenuItem>
                            <MenuItem value={24}>過去24ヶ月</MenuItem>
                          </Select>
                        </Box>
                      </Box>

                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                          data={categoryTrendChartData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis
                            domain={[0, 5]}
                            ticks={[0, 1, 2, 3, 4, 5]}
                            label={{ value: '平均スコア', angle: -90, position: 'insideLeft' }}
                          />
                          <RechartsTooltip
                            formatter={(value: any) => value !== null ? value.toFixed(2) : 'N/A'}
                            labelFormatter={(label: string) => `期間: ${label}`}
                          />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} />
                          {categoryTrendData.categories.map((category: any, index: number) => {
                            const colors = ['#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336', '#00bcd4', '#ff5722', '#3f51b5', '#009688', '#ffc107']
                            return (
                              <Line
                                key={category.categoryId}
                                type="monotone"
                                dataKey={category.categoryId}
                                name={category.categoryName}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls
                              />
                            )
                          })}
                        </LineChart>
                      </ResponsiveContainer>

                      {categoryTrendData.dateRange && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                          データ期間: {categoryTrendData.dateRange.start} 〜 {categoryTrendData.dateRange.end}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
                */}


                {/* スキル申請・承認進捗（簡略版） */}
                {skillProgress && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          スキル申請・承認進捗
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          承認済み: {Number(skillProgress.statusCounts?.approved || 0)} 件 ({Number(skillProgress.progressRate?.approved || 0).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* 凡例 */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Chip label="S: エキスパート" size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
                  <Chip label="A: 指導レベル" size="small" sx={{ bgcolor: '#2196f3', color: 'white' }} />
                  <Chip label="B: 独力レベル" size="small" sx={{ bgcolor: '#4caf50', color: 'white' }} />
                  <Chip label="C: 補助レベル" size="small" sx={{ bgcolor: '#ff9800', color: 'white' }} />
                  <Chip label="D: 知識レベル" size="small" sx={{ bgcolor: '#9e9e9e', color: 'white' }} />
                </Box>
              </>
            )}

            {!skillMapLoading && !skillMapData && (
              <Alert severity="info">
                フィルタを選択してスキルマップを表示してください。
              </Alert>
            )}
                </Box>
              )}
      </Card>

      {/* Employee History Dialog */}
      <Dialog
        open={employeeHistoryDialog}
        onClose={() => setEmployeeHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedEmployeeName}の評価履歴</DialogTitle>
        <DialogContent>
          {selectedEmployeeHistory.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>評価期間</TableCell>
                    <TableCell align="right">P点数</TableCell>
                    <TableCell align="right">昇給率 (%)</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>更新日</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmployeeHistory.map((history, index) => {
                    const statusInfo = getStatusLabel(history.status as EvaluationStatus)
                    return (
                      <TableRow key={index}>
                        <TableCell>{history.evaluation_period}</TableCell>
                        <TableCell align="right">{formatNumber(history.performance_score)}</TableCell>
                        <TableCell align="right">
                          {canViewSalaryRate(
                            history.status as EvaluationStatus,
                            user?.role || 'employee',
                            true // 評価者として見ている履歴
                          ) ? (
                            formatNumber(history.salary_increase_rate)
                          ) : (
                            <Tooltip title="昇給率は評価確定後に表示されます">
                              <Box component="span" sx={{ color: 'text.disabled' }}>非表示</Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.chipColor}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(history.updated_at)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">履歴データがありません</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeHistoryDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* My Evaluation Detail Dialog */}
      <Dialog
        open={showMyDetail}
        onClose={() => setShowMyDetail(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>評価詳細（閲覧専用）</DialogTitle>
        <DialogContent>
          {myEvaluationData && (
            <EvaluationDetailView
              employee={myEvaluationData.employee}
              evaluation={myEvaluationData.evaluation}
              evaluationItems={myEvaluationData.items || []}
              salesMetrics={myEvaluationData.salesMetrics}
              evaluators={myEvaluationData.evaluators || []}
              evaluatorScores={myEvaluationData.evaluatorScores || myEvaluationData.evaluator_scores || []}
              evaluatorItemScores={myEvaluationData.evaluatorItemScores || myEvaluationData.evaluator_item_scores || []}
              goal={myEvaluationData.goal}
              showApprovalSection={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMyDetail(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Employee Detail Dialog */}
      <Dialog
        open={employeeDetailDialog}
        onClose={() => setEmployeeDetailDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>評価詳細（閲覧専用）</DialogTitle>
        <DialogContent>
          {selectedEmployeeDetail && (
            <EvaluationDetailView
              employee={selectedEmployeeDetail.employee}
              evaluation={selectedEmployeeDetail.evaluation}
              evaluationItems={selectedEmployeeDetail.evaluation_items || []}
              salesMetrics={selectedEmployeeDetail.sales_metrics}
              evaluators={selectedEmployeeDetail.evaluators || []}
              evaluatorScores={selectedEmployeeDetail.evaluatorScores || selectedEmployeeDetail.evaluator_scores || []}
              evaluatorItemScores={selectedEmployeeDetail.evaluatorItemScores || selectedEmployeeDetail.evaluator_item_scores || []}
              goal={selectedEmployeeDetail.goal}
              showApprovalSection={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeDetailDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 従業員履歴書ダイアログ */}
      <Dialog
        open={resumeDialogOpen}
        onClose={() => {
          setResumeDialogOpen(false)
          setSelectedEmployee(null)
          setEmployeeResumeData(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            従業員スキル履歴書
            {selectedEmployee && (
              <Typography variant="subtitle1" color="text.secondary">
                {selectedEmployee.name} - {selectedEmployee.department}
              </Typography>
            )}
          </Box>
          <Box>
            <Tooltip title="印刷">
              <IconButton
                onClick={() => {
                  const printContent = document.getElementById('resume-print-content')
                  if (printContent) {
                    const printWindow = window.open('', '_blank')
                    if (printWindow) {
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>従業員スキル履歴書 - ${selectedEmployee?.name || ''}</title>
                            <style>
                              body { font-family: 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif; padding: 20px; }
                              h1 { font-size: 18px; border-bottom: 2px solid #333; padding-bottom: 8px; }
                              h2 { font-size: 14px; margin-top: 20px; border-bottom: 1px solid #999; padding-bottom: 4px; }
                              table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                              th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; font-size: 12px; }
                              th { background-color: #f5f5f5; }
                              .skill-level { display: inline-block; width: 20px; height: 20px; text-align: center; border-radius: 4px; color: #fff; font-weight: bold; }
                              .level-S { background-color: #9c27b0; }
                              .level-A { background-color: #2196f3; }
                              .level-B { background-color: #4caf50; }
                              .level-C { background-color: #ff9800; }
                              .level-D { background-color: #9e9e9e; }
                              @page { margin: 15mm; }
                            </style>
                          </head>
                          <body>
                            ${printContent.innerHTML}
                          </body>
                        </html>
                      `)
                      printWindow.document.close()
                      printWindow.print()
                    }
                  }
                }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => {
              setResumeDialogOpen(false)
              setSelectedEmployee(null)
              setEmployeeResumeData(null)
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {resumeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box id="resume-print-content">
              {/* 基本情報 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                基本情報
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', width: '20%' }}>氏名</TableCell>
                      <TableCell>{selectedEmployee?.name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', width: '20%' }}>社員ID</TableCell>
                      <TableCell>{selectedEmployee?.id || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>所属部門</TableCell>
                      <TableCell colSpan={3}>{selectedEmployee?.department || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* スキル一覧 */}
              <Typography variant="h6" gutterBottom>
                スキル一覧
              </Typography>
              {employeeResumeData?.skills && employeeResumeData.skills.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>カテゴリ</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>スキル名</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }} align="center">レベル</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeeResumeData.skills.map((skill: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{skill.category_name || skill.categoryName || '-'}</TableCell>
                          <TableCell>{skill.item_name || skill.itemName || skill.name || '-'}</TableCell>
                          <TableCell align="center">
                            <Box
                              className={`skill-level level-${skill.skill_level || skill.level || 'D'}`}
                              sx={{
                                display: 'inline-block',
                                width: 24,
                                height: 24,
                                lineHeight: '24px',
                                textAlign: 'center',
                                borderRadius: 1,
                                color: '#fff',
                                fontWeight: 'bold',
                                bgcolor: 
                                  (skill.skill_level || skill.level) === 'S' ? '#9c27b0' :
                                  (skill.skill_level || skill.level) === 'A' ? '#2196f3' :
                                  (skill.skill_level || skill.level) === 'B' ? '#4caf50' :
                                  (skill.skill_level || skill.level) === 'C' ? '#ff9800' : '#9e9e9e'
                              }}
                            >
                              {skill.skill_level || skill.level || 'D'}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ mb: 3 }}>スキル情報がありません</Typography>
              )}

              {/* プロジェクト履歴 */}
              <Typography variant="h6" gutterBottom>
                プロジェクト履歴
              </Typography>
              {employeeResumeData?.projects && employeeResumeData.projects.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 250, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>プロジェクト名</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>役割</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>期間</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeeResumeData.projects.map((proj: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{proj.project_name || proj.name || '-'}</TableCell>
                          <TableCell>{proj.role || proj.responsibilities || '-'}</TableCell>
                          <TableCell>
                            {proj.start_date ? formatDateJST(proj.start_date) : '-'}
                            {proj.end_date ? ` 〜 ${formatDateJST(proj.end_date)}` : ' 〜 現在'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ mb: 3 }}>プロジェクト履歴がありません</Typography>
              )}

              {/* 資格情報 */}
              <Typography variant="h6" gutterBottom>
                資格情報
              </Typography>
              {employeeResumeData?.certifications && employeeResumeData.certifications.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>資格名</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>取得日</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeeResumeData.certifications.map((cert: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{cert.certification_name || cert.name || '-'}</TableCell>
                          <TableCell>
                            {cert.acquired_date ? formatDateJST(cert.acquired_date) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">資格情報がありません</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => {
              const printContent = document.getElementById('resume-print-content')
              if (printContent) {
                const printWindow = window.open('', '_blank')
                if (printWindow) {
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>従業員スキル履歴書 - ${selectedEmployee?.name || ''}</title>
                        <style>
                          body { font-family: 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif; padding: 20px; }
                          h1 { font-size: 18px; border-bottom: 2px solid #333; padding-bottom: 8px; }
                          h2 { font-size: 14px; margin-top: 20px; border-bottom: 1px solid #999; padding-bottom: 4px; }
                          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; font-size: 12px; }
                          th { background-color: #f5f5f5; }
                          .skill-level { display: inline-block; width: 20px; height: 20px; text-align: center; border-radius: 4px; color: #fff; font-weight: bold; }
                          .level-S { background-color: #9c27b0; }
                          .level-A { background-color: #2196f3; }
                          .level-B { background-color: #4caf50; }
                          .level-C { background-color: #ff9800; }
                          .level-D { background-color: #9e9e9e; }
                          @page { margin: 15mm; }
                        </style>
                      </head>
                      <body>
                        ${printContent.innerHTML}
                      </body>
                    </html>
                  `)
                  printWindow.document.close()
                  printWindow.print()
                }
              }
            }}
          >
            印刷
          </Button>
          <Button onClick={() => {
            setResumeDialogOpen(false)
            setSelectedEmployee(null)
            setEmployeeResumeData(null)
          }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReportPage
